<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'

const { t } = useI18n()
const currentYear = new Date().getFullYear()

const colorMode = useColorMode()

const logoSrc = computed(() => {
  return colorMode.preference === 'dark' ? '/assets/zzz/logo-white.png' : '/assets/zzz/logo-black.png'
})

// Make link labels reactive using computed properties
const solutionLinks = computed(() => [
  { label: t('footer.sections.solutions.volumeMeasurement'), to: '/solutions/volume' },
  { label: t('footer.sections.solutions.smartTraffic'), to: '/solutions/traffic' },
  { label: t('footer.sections.solutions.3dRecognition'), to: '/solutions/recognition' }
])

const navigationLinks = computed(() => [
  { label: t('footer.sections.navigation.home'), to: '/' },
  { label: t('footer.sections.navigation.products'), to: '/products' },
  { label: t('footer.sections.navigation.about'), to: '/enterprise' },
  { label: t('footer.sections.navigation.solutions'), to: '/solutions' }
])

// Placeholder data for social links - replace with actual links
const socialLinks = [
  { icon: 'i-mdi-wechat', name: 'WeChat', link: 'https://www.zhihu.com/people/hui-yan-87-36' },
  { icon: 'i-mdi-sina-weibo', name: 'Weibo', link: 'https://www.zhihu.com/people/hui-yan-87-36' },
  { icon: 'i-simple-icons-zhihu', name: 'Zhihu', link: 'https://www.zhihu.com/people/hui-yan-87-36' }
]
</script>

<template>
  <UFooter :ui="{ top: 'border-b border-default' }">
    <template #top>
      <UContainer class="grid grid-cols-1 lg:grid-cols-4 gap-8 lg:gap-16">
        <!-- 左边模块: Logo 和联系信息 -->
        <div class="flex flex-col gap-4">
          <img
            ref="logo"
            :src="logoSrc"
            class="object-cover object-top"
            alt="template logo"
            width="132"
            loading="lazy"
          />
          <p class="text-muted text-sm">{{ t('footer.address') }}</p>

          <p class="text-muted text-sm">contact@hclidar.com</p>

          <p class="text-muted text-sm">李经理 +86 18250856491</p>
          <p class="text-muted text-sm" style="margin-top: -10px;">曹经理 +86 13666096406</p>
        </div>

        <!-- 中间模块: 解决方案、应用场景 -->
        <div class="grid grid-cols-2 gap-8 lg:col-span-2">
          <div>
            <h3 class="text-base font-semibold text-gray-900 dark:text-white mb-3">{{ t('footer.sections.solutions.label') }}</h3>

            <ul class="space-y-3">
              <li v-for="link in solutionLinks" :key="link.to">
                <NuxtLink :to="link.to" class="text-muted text-sm hover:text-gray-700 dark:hover:text-gray-200">
                  {{ link.label }}
                </NuxtLink>
              </li>
            </ul>
          </div>
          <div>
            <h3 class="text-base font-semibold text-gray-900 dark:text-white mb-3">{{ t('footer.sections.navigation.label') }}</h3>

            <ul class="space-y-3">
              <li v-for="link in navigationLinks" :key="link.to">
                <NuxtLink :to="link.to" class="text-muted text-sm hover:text-gray-700 dark:hover:text-gray-200">
                  {{ link.label }}
                </NuxtLink>
              </li>
            </ul>
          </div>
        </div>

        <!-- 右边模块: 关注我们、扫码 -->
        <div class="flex flex-col justify-center items-center" style="margin-right: 30px;">
          <h3 class="text-base font-semibold text-gray-900 dark:text-white mb-3">{{ t('footer.followUs') }}</h3>

          <div class="flex space-x-3 mb-4">
            <UButton
              v-for="social in socialLinks"
              :key="social.name"
              :to="social.link"
              target="_blank"
              variant="ghost"
              color="neutral"
              :icon="social.icon"
              size="md"
            />
          </div>
          <!-- Replace with actual QR code image -->
          <div class="w-32 h-32 bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-muted text-xs">
            <img src="/assets/zzz/ewm.png" alt="QR Code">
          </div>
        </div>
      </UContainer>
    </template>

    <!-- Bottom Section: Copyright and Privacy Policy -->
    <template #left>
      <p class="text-muted text-sm">
        {{ t('footer.copyright', { year: currentYear }) }}
      </p>
    </template>

    <template #right>
      <NuxtLink to="" class="text-muted text-sm hover:underline">
        {{ t('footer.privacyPolicy') }}
      </NuxtLink>
    </template>
  </UFooter>
</template>
